import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { enhancedAIMatchingService as aiMatchingService } from '../../services/enhancedAIMatchingService';
import { extractSkillNames, normalizeSkill } from '../../utils/skillsUtils';
import { supabase } from '../../utils/supabase';
import useFreeUserMatchLimiter, { FreeUserMatchLimitBanner, UpgradeToViewMoreCard } from '../../hooks/useFreeUserMatchLimiter.jsx';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import AIMatchingSkeleton from '../../components/ai-matching/AIMatchingSkeleton';
import CandidateCardSkeleton from '../../components/ai-matching/CandidateCardSkeleton';
import FreeTierUpgradePrompt from '../../components/ai-matching/FreeTierUpgradePrompt';

/**
 * AI Matching Page - Optimized v2.0
 * 
 * Performance Optimizations:
 * - Redis distributed caching (via enhancedAIMatchingService v4.0)
 * - Debounced search with useDeferredValue
 * - Memoized filtering and sorting to prevent unnecessary recalculations
 * - Lazy loading skeleton states with staggered animations
 * - Parallel API calls with Promise.allSettled for resilience
 * - Query result memoization with stable dependency arrays
 * - Optimized re-renders with useCallback and useMemo
 */

const AIMatchingPage = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [matchingData, setMatchingData] = useState({});
  const [aiInsights, setAiInsights] = useState({});
  const [error, setError] = useState(null);
  
  // Free tier match limiter - tracks matches viewed by free-tier users
  const { viewedMatchCount, canViewMore, remainingMatches, recordMatchViewed, isFreeTier } = useFreeUserMatchLimiter();
  
  // Sort and Filter state
  const [sortBy, setSortBy] = useState('match_score'); // 'match_score', 'rating', 'hourly_rate', 'experience_years', 'name'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [filters, setFilters] = useState({
    minMatchScore: 0,
    maxMatchScore: 100,
    minRating: 0,
    maxRating: 5,
    minHourlyRate: 0,
    maxHourlyRate: 100,
    minExperience: 0,
    maxExperience: 20,
    skills: [],
    availability: 'all', // 'all', 'available', 'busy', 'unavailable'
    location: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery); // Debounce search
  const loadingRef = useRef(false); // Prevent duplicate loads
  const dataLoadedRef = useRef(false); // Track if data was loaded

  const tabs = [
    { id: 'recommendations', label: 'Recommendations', icon: 'Sparkles' },
    { id: 'smart-search', label: 'Smart Search', icon: 'Search', comingSoon: true },
    { id: 'insights', label: 'AI Insights', icon: 'Brain', comingSoon: true },
    { id: 'predictions', label: 'Predictions', icon: 'TrendingUp', comingSoon: true }
  ];

  // Load real AI data from Supabase with optimization
  const loadAIData = useCallback(async (forceRefresh = false) => {
    if (!userProfile?.id || userProfile.role !== 'employer') {
      setLoading(false);
      return;
    }

    // Prevent duplicate loads
    if (loadingRef.current && !forceRefresh) {
      console.log('[AI Matching] Load already in progress, skipping...');
      return;
    }

    if (dataLoadedRef.current && !forceRefresh) {
      console.log('[AI Matching] Data already loaded, skipping...');
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[AI Matching] Loading data for employer:', userProfile.id);

      // Use Promise.allSettled for resilience - partial failures won't block the page
      const results = await Promise.allSettled([
        aiMatchingService.getCandidateRecommendations(userProfile.id, { forceRefresh }),
        aiMatchingService.getSmartFilters(userProfile.id),
        aiMatchingService.getMarketInsights(),
        aiMatchingService.getHiringPredictions(userProfile.id)
      ]);

      const [
        recommendationsResult,
        filtersResult,
        insightsResult,
        predictionsResult
      ] = results;

      console.log('[AI Matching] Results:', {
        recommendations: recommendationsResult.status,
        filters: filtersResult.status,
        insights: insightsResult.status,
        predictions: predictionsResult.status
      });

      // Always use real data from the database (handle both fulfilled and rejected)
      setMatchingData({
        recommendations: recommendationsResult.status === 'fulfilled' && recommendationsResult.value?.success 
          ? recommendationsResult.value.data 
          : [],
        smart_filters: filtersResult.status === 'fulfilled' && filtersResult.value?.success 
          ? filtersResult.value.data 
          : null,
        market_insights: insightsResult.status === 'fulfilled' && insightsResult.value?.success 
          ? insightsResult.value.data 
          : null
      });

      // Generate success factors from real data
      const successFactorsResult = await aiMatchingService.generateSuccessFactorsFromData(userProfile.id);

      setAiInsights({
        hiring_predictions: predictionsResult.status === 'fulfilled' && predictionsResult.value?.success 
          ? predictionsResult.value.data 
          : null,
        optimization_suggestions: generateOptimizationSuggestions(
          recommendationsResult.status === 'fulfilled' && recommendationsResult.value?.success 
            ? recommendationsResult.value.data 
            : []
        ),
        success_factors: successFactorsResult?.success ? successFactorsResult.factors : []
      });

      dataLoadedRef.current = true; // Mark as loaded

    } catch (err) {
      console.error('[AI Matching] Error loading data:', err);
      setError(err.message);
      
      // Set empty data structure on error instead of mock data
      setMatchingData({
        recommendations: [],
        smart_filters: null,
        market_insights: null
      });
      setAiInsights({
        hiring_predictions: null,
        optimization_suggestions: [],
        success_factors: []
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [userProfile?.id, userProfile?.role]);

  useEffect(() => {
    loadAIData();
  }, [loadAIData]);

  // Memoized sort function
  const sortCandidates = useCallback((candidates, sortBy, sortOrder) => {
    if (!candidates || candidates.length === 0) return [];
    
    const sorted = [...candidates].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'match_score':
          aValue = a.match_score || 0;
          bValue = b.match_score || 0;
          break;
        case 'rating':
          aValue = a.candidate?.rating || 0;
          bValue = b.candidate?.rating || 0;
          break;
        case 'hourly_rate':
          aValue = a.candidate?.hourly_rate || 0;
          bValue = b.candidate?.hourly_rate || 0;
          break;
        case 'experience_years':
          aValue = a.candidate?.experience_years || 0;
          bValue = b.candidate?.experience_years || 0;
          break;
        case 'name':
          aValue = a.candidate?.name?.toLowerCase() || '';
          bValue = b.candidate?.name?.toLowerCase() || '';
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    return sorted;
  }, []);

  // Memoized filter function
  const filterCandidates = useCallback((candidates, filters, searchQuery) => {
    if (!candidates || candidates.length === 0) return [];
    
    return candidates.filter(candidate => {
      const c = candidate.candidate;
      
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const candidateSkillNames = extractSkillNames(c.skills || []);
        const matchesSearch = 
          c.name?.toLowerCase().includes(query) ||
          c.title?.toLowerCase().includes(query) ||
          c.location?.toLowerCase().includes(query) ||
          candidateSkillNames.some(skillName => skillName.toLowerCase().includes(query));
        
        if (!matchesSearch) return false;
      }
      
      // Match score filter
      if (candidate.match_score < filters.minMatchScore || candidate.match_score > filters.maxMatchScore) {
        return false;
      }
      
      // Rating filter
      if (c.rating < filters.minRating || c.rating > filters.maxRating) {
        return false;
      }
      
      // Hourly rate filter
      if (c.hourly_rate < filters.minHourlyRate || c.hourly_rate > filters.maxHourlyRate) {
        return false;
      }
      
      // Experience filter
      if (c.experience_years < filters.minExperience || c.experience_years > filters.maxExperience) {
        return false;
      }
      
      // Skills filter
      if (filters.skills.length > 0) {
        const candidateSkillNames = extractSkillNames(c.skills || []);
        const hasRequiredSkills = filters.skills.every(requiredSkill =>
          candidateSkillNames.some(skillName => 
            skillName.toLowerCase().includes(requiredSkill.toLowerCase())
          )
        );
        if (!hasRequiredSkills) return false;
      }
      
      // Availability filter
      if (filters.availability !== 'all' && c.availability !== filters.availability) {
        return false;
      }
      
      // Location filter
      if (filters.location !== 'all' && !c.location?.toLowerCase().includes(filters.location.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, []);

  // Memoized filtered and sorted candidates for optimal performance
  const filteredAndSortedCandidates = useMemo(() => {
    if (!matchingData.recommendations) return [];
    
    // Use deferred search query for debouncing
    const filtered = filterCandidates(matchingData.recommendations, filters, deferredSearchQuery);
    const sorted = sortCandidates(filtered, sortBy, sortOrder);
    
    console.log(`[AI Matching] Filtered ${filtered.length}/${matchingData.recommendations.length} candidates`);
    return sorted;
  }, [matchingData.recommendations, filters, deferredSearchQuery, sortBy, sortOrder, filterCandidates, sortCandidates]);

  const getFilteredAndSortedCandidates = useCallback(() => {
    return filteredAndSortedCandidates;
  }, [filteredAndSortedCandidates]);

  const _handleSortChange = useCallback((newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc'); // Default to descending for new sort
    }
  }, [sortBy]);

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      minMatchScore: 0,
      maxMatchScore: 100,
      minRating: 0,
      maxRating: 5,
      minHourlyRate: 0,
      maxHourlyRate: 100,
      minExperience: 0,
      maxExperience: 20,
      skills: [],
      availability: 'all',
      location: 'all'
    });
    setSearchQuery('');
  };

  // Helper function to format skills for display
  const formatSkillForDisplay = (skill) => {
    // Handle legacy string format
    if (typeof skill === 'string') {
      return {
        name: skill,
        rating: null,
        proficiency: null,
        isUpSkilled: false
      };
    }
    
    // Handle up-skills object format
    if (typeof skill === 'object' && skill !== null) {
      const normalizedSkill = normalizeSkill(skill);
      return {
        name: normalizedSkill.name,
        rating: normalizedSkill.rating > 0 ? normalizedSkill.rating : null,
        proficiency: normalizedSkill.proficiency,
        isUpSkilled: normalizedSkill.upSkillsFormat || normalizedSkill.databaseSkill,
        verified: normalizedSkill.verified,
        experience: normalizedSkill.experience
      };
    }
    
    return { name: 'Unknown Skill', rating: null, proficiency: null, isUpSkilled: false };
  };

  // Memoized helper functions for better performance
  const uniqueSkills = useMemo(() => {
    if (!matchingData.recommendations) return [];
    
    const allSkills = matchingData.recommendations.flatMap(rec => {
      if (!rec.candidate?.skills) return [];
      return rec.candidate.skills.map(skill => {
        const formatted = formatSkillForDisplay(skill);
        return formatted.name;
      });
    });
    
    return [...new Set(allSkills)].sort();
  }, [matchingData.recommendations]);

  const uniqueLocations = useMemo(() => {
    if (!matchingData.recommendations) return [];
    
    const allLocations = matchingData.recommendations.map(rec => 
      rec.candidate?.location
    ).filter(Boolean);
    
    return [...new Set(allLocations)].sort();
  }, [matchingData.recommendations]);

  const getUniqueSkills = useCallback(() => uniqueSkills, [uniqueSkills]);
  const getUniqueLocations = useCallback(() => uniqueLocations, [uniqueLocations]);

  // Helper function to generate optimization suggestions based on real data
  const generateOptimizationSuggestions = (recommendations) => {
    const suggestions = [];

    if (!recommendations || recommendations.length === 0) {
      suggestions.push({
        category: 'Job Posting',
        suggestion: 'Create active job postings to start receiving AI-powered recommendations',
        impact: 'High',
        effort: 'Low'
      });
    } else {
      const avgScore = recommendations.reduce((sum, rec) => sum + rec.match_score, 0) / recommendations.length;
      
      if (avgScore < 80) {
        suggestions.push({
          category: 'Job Requirements',
          suggestion: 'Consider broadening skill requirements to attract more qualified candidates',
          impact: 'Medium',
          effort: 'Low'
        });
      }

      suggestions.push({
        category: 'Response Time',
        suggestion: 'Respond to applications within 24 hours to increase acceptance rates',
        impact: 'High',
        effort: 'Low'
      });
    }

    return suggestions;
  };

  // Helper function to generate success factors
  // Helper function to generate success factors
  const generateSuccessFactors = () => {
    return [
      { factor: 'Clear job requirements', importance: 92, your_score: 85 },
      { factor: 'Competitive compensation', importance: 88, your_score: 78 },
      { factor: 'Company culture description', importance: 76, your_score: 90 },
      { factor: 'Growth opportunities', importance: 71, your_score: 82 }
    ];
  };

  // Memoized components for better performance
  const MatchScoreBar = React.memo(({ score, size = 'md' }) => {
    const getScoreColor = (score) => {
      if (score >= 90) return 'bg-green-500';
      if (score >= 80) return 'bg-blue-500';
      if (score >= 70) return 'bg-yellow-500';
      return 'bg-red-500';
    };

    const barHeight = size === 'sm' ? 'h-2' : 'h-3';
    
    return (
      <div className={`w-full bg-secondary-200 rounded-full ${barHeight}`}>
        <div
          className={`${getScoreColor(score)} ${barHeight} rounded-full transition-all duration-500`}
          style={{ width: `${score}%` }}
        ></div>
      </div>
    );
  });

  const CandidateRecommendationCard = React.memo(({ recommendation }) => {
    const handleCardClick = async (e) => {
      // Don't navigate if clicking on buttons or interactive elements
      if (e.target.closest('button')) {
        return;
      }
      
      // Get candidate ID
      const candidateId = recommendation.candidate?.id || recommendation.candidate?.user_id;
      if (!candidateId) {
        console.error('No candidate ID found');
        return;
      }

      try {
        // First fetch the public username from the database
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('public_username, is_public_profile')
          .eq('id', candidateId)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          // Fallback to contact form if we can't get public username
          navigate(`/contact-va/${candidateId}`);
          return;
        }

        // Check if profile is public and has username
        if (profile?.is_public_profile && profile?.public_username) {
          navigate(`/va/${profile.public_username}`);
        } else {
          // Fallback to contact form for non-public profiles
          navigate(`/contact-va/${candidateId}`);
        }
      } catch (error) {
        console.error('Error navigating to profile:', error);
        // Fallback to contact form on any error
        navigate(`/contact-va/${candidateId}`);
      }
    };

    return (
      <div 
        className="bg-surface rounded-lg border border-border p-6 hover:shadow-lg transition-shadow duration-200 cursor-pointer" 
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <img
              src={recommendation.candidate?.avatar || '/api/placeholder/64/64'}
              alt={recommendation.candidate?.name || 'Candidate'}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {recommendation.candidate?.name || 'Anonymous VA'}
              </h3>
              <p className="text-text-secondary">{recommendation.candidate?.title || 'Virtual Assistant'}</p>
              <div className="flex items-center space-x-4 mt-1">
                <span className="text-sm text-text-muted">{recommendation.candidate?.location || 'Remote'}</span>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-yellow-500 fill-current" />
                  <span className="text-sm font-medium">{recommendation.candidate?.rating || 4.0}</span>
                  <span className="text-sm text-text-muted">({recommendation.candidate?.total_reviews || 0})</span>
                </div>
              </div>
            </div>
          </div>
        <div className="text-right">
          <div className="flex items-center space-x-2 mb-2 relative group">
            <Icon name="Sparkles" size={16} className="text-primary" />
            <span className="text-2xl font-bold text-primary cursor-help">{recommendation.match_score || 85}%</span>
            
            {/* Match Score Calculation Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-64 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 shadow-lg">
              <div className="font-semibold mb-2 border-b border-gray-700 pb-2">Match Score Breakdown</div>
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span>Skills Match</span>
                  <span className="font-medium">25%</span>
                </div>
                <div className="flex justify-between">
                  <span>Experience Match</span>
                  <span className="font-medium">20%</span>
                </div>
                <div className="flex justify-between">
                  <span>Rate Compatibility</span>
                  <span className="font-medium">15%</span>
                </div>
                <div className="flex justify-between">
                  <span>Quality Score</span>
                  <span className="font-medium">15%</span>
                </div>
                <div className="flex justify-between">
                  <span>Location Match</span>
                  <span className="font-medium">10%</span>
                </div>
                <div className="flex justify-between">
                  <span>Work Type Match</span>
                  <span className="font-medium">10%</span>
                </div>
                <div className="flex justify-between">
                  <span>Industry Match</span>
                  <span className="font-medium">5%</span>
                </div>
              </div>
              {/* Tooltip Arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>
          <p className="text-sm text-text-secondary">AI Match Score</p>
        </div>
      </div>

      <div className="mb-4">
        <MatchScoreBar score={recommendation.match_score || 85} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <h4 className="font-medium text-text-primary mb-2">Key Skills</h4>
          <div className="flex flex-wrap gap-2">
            {(recommendation.candidate.skills || []).slice(0, 4).map((skill, index) => {
              const skillInfo = formatSkillForDisplay(skill);
              return (
                <div key={index} className="relative group">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    skillInfo.isUpSkilled 
                      ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                      : 'bg-primary-100 text-primary border border-primary-200'
                  }`}>
                    {skillInfo.name}
                    {skillInfo.rating && (
                      <span className="ml-1 text-yellow-600">
                        {'★'.repeat(skillInfo.rating)}
                      </span>
                    )}
                    {skillInfo.verified && (
                      <span className="ml-1 text-green-600" title="Verified Skill">✓</span>
                    )}
                  </span>
                  
                  {/* Enhanced tooltip for up-skilled items */}
                  {skillInfo.isUpSkilled && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      {skillInfo.rating && (
                        <div>Rating: {skillInfo.rating}/5 stars</div>
                      )}
                      {skillInfo.proficiency && (
                        <div>Proficiency: {skillInfo.proficiency}%</div>
                      )}
                      {skillInfo.verified && (
                        <div>✓ Verified Skill</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {(recommendation.candidate.skills || []).length > 4 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                +{(recommendation.candidate.skills || []).length - 4} more
              </span>
            )}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-text-primary mb-2">AI Insights</h4>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">Success Probability</span>
              <span className="font-medium text-green-600">
                {recommendation.ai_insights?.success_probability || 75}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">Cultural Fit</span>
              <span className="font-medium">
                {recommendation.ai_insights?.cultural_fit || 'Good'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h4 className="font-medium text-text-primary mb-2">Why This Match?</h4>
        <ul className="space-y-1">
          {(recommendation.match_reasons || ['Strong skill alignment', 'Excellent experience match']).slice(0, 2).map((reason, index) => (
            <li key={index} className="flex items-start space-x-2 text-sm text-text-secondary">
              <Icon name="Check" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <span className="text-lg font-semibold text-text-primary">
            ${recommendation.candidate?.hourly_rate || 25}/hr
          </span>
          <span className="text-sm text-text-secondary">
            {recommendation.candidate?.availability || 'Available'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            onClick={() => navigate(`/messages?candidate=${recommendation.candidate?.name || recommendation.id}`)}
          >
            Message
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconName="Calendar"
            onClick={() => navigate(`/interview-management?schedule=${recommendation.id || 'new'}`)}
          >
            Interview
          </Button>
        </div>
      </div>
    </div>
    );
  });

  const renderRecommendations = () => {
    if (loading) {
      return <AIMatchingSkeleton section="recommendations" itemCount={6} />;
    }

    if (error) {
      return (
        <div className="space-y-6">
          <div className="bg-red-50 rounded-lg border border-red-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="AlertCircle" size={24} className="text-red-600" />
              <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              iconName="RefreshCw"
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6 border border-primary-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">AI-Powered Recommendations</h3>
                <p className="text-text-secondary">
                  Live data from your jobs and platform candidates
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="inline-flex items-center space-x-2 px-3 py-1 bg-green-100 rounded-full border border-green-200">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-800">Live Data</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary mb-1">
                {matchingData.recommendations?.length > 0 
                  ? Math.round(matchingData.recommendations.reduce((sum, rec) => sum + (rec.match_score || 0), 0) / matchingData.recommendations.length)
                  : '0'
                }%
              </div>
              <div className="text-sm text-text-secondary">Average Match Score</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600 mb-1">
                {matchingData.recommendations?.length > 0 
                  ? Math.round(matchingData.recommendations.reduce((sum, rec) => sum + (rec.ai_insights?.success_probability || 0), 0) / matchingData.recommendations.length)
                  : '0'
                }%
              </div>
              <div className="text-sm text-text-secondary">Success Probability</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {matchingData.recommendations?.length || 0}
              </div>
              <div className="text-sm text-text-secondary">
                AI Recommendations
              </div>
            </div>
          </div>
        </div>

        {(!matchingData.recommendations || matchingData.recommendations.length === 0) ? (
          <div className="bg-surface rounded-lg border border-border p-8 text-center">
            <Icon name="Users" size={48} className="text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Recommendations Yet</h3>
            <p className="text-text-secondary mb-4">
              Create active job postings to start receiving AI-powered candidate recommendations.
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/job-posting')}
              iconName="Plus"
            >
              Create Job Posting
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Sort and Filter Controls */}
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                {/* Search Bar */}
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
                    <input
                      type="text"
                      placeholder="Search candidates by name, skills, location..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Sort and Filter Buttons */}
                <div className="flex items-center space-x-3">
                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [newSortBy, newSortOrder] = e.target.value.split('-');
                        setSortBy(newSortBy);
                        setSortOrder(newSortOrder);
                      }}
                      className="appearance-none bg-surface border border-border rounded-lg px-4 py-2 pr-8 text-sm font-medium text-text-primary focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="match_score-desc">Best Match</option>
                      <option value="rating-desc">Highest Rated</option>
                      <option value="hourly_rate-asc">Lowest Rate</option>
                      <option value="hourly_rate-desc">Highest Rate</option>
                      <option value="experience_years-desc">Most Experience</option>
                      <option value="name-asc">Name A-Z</option>
                    </select>
                    <Icon name="ChevronDown" size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary pointer-events-none" />
                  </div>

                  {/* Filter Toggle */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      showFilters 
                        ? 'bg-primary-100 text-primary-700 border border-primary-200' 
                        : 'bg-surface border border-border text-text-primary hover:bg-surface-hover'
                    }`}
                  >
                    <Icon name="Filter" size={16} />
                    <span>Filters</span>
                    {Object.values(filters).some(v => 
                      (Array.isArray(v) && v.length > 0) || 
                      (typeof v === 'string' && v !== 'all') ||
                      (typeof v === 'number' && (
                        (filters.minMatchScore > 0) || 
                        (filters.maxMatchScore < 100) ||
                        (filters.minRating > 0) ||
                        (filters.maxRating < 5) ||
                        (filters.minHourlyRate > 0) ||
                        (filters.maxHourlyRate < 100) ||
                        (filters.minExperience > 0) ||
                        (filters.maxExperience < 20)
                      ))
                    ) && (
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    )}
                  </button>

                  {/* Clear Filters */}
                  {(searchQuery || Object.values(filters).some(v => 
                    (Array.isArray(v) && v.length > 0) || 
                    (typeof v === 'string' && v !== 'all') ||
                    (typeof v === 'number' && (
                      (filters.minMatchScore > 0) || 
                      (filters.maxMatchScore < 100) ||
                      (filters.minRating > 0) ||
                      (filters.maxRating < 5) ||
                      (filters.minHourlyRate > 0) ||
                      (filters.maxHourlyRate < 100) ||
                      (filters.minExperience > 0) ||
                      (filters.maxExperience < 20)
                    ))
                  )) && (
                    <button
                      onClick={clearFilters}
                      className="inline-flex items-center space-x-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary"
                    >
                      <Icon name="X" size={14} />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Advanced Filters Panel */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Match Score Range */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Match Score
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={filters.minMatchScore}
                            onChange={(e) => handleFilterChange('minMatchScore', parseInt(e.target.value))}
                            className="flex-1"
                          />
                        </div>
                        <div className="text-xs text-text-secondary">
                          {filters.minMatchScore}% - {filters.maxMatchScore}%
                        </div>
                      </div>
                    </div>

                    {/* Rating Range */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Minimum Rating
                      </label>
                      <select
                        value={filters.minRating}
                        onChange={(e) => handleFilterChange('minRating', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      >
                        <option value="0">Any Rating</option>
                        <option value="3">3+ Stars</option>
                        <option value="4">4+ Stars</option>
                        <option value="4.5">4.5+ Stars</option>
                        <option value="5">5 Stars Only</option>
                      </select>
                    </div>

                    {/* Hourly Rate Range */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Hourly Rate ($)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.minHourlyRate || ''}
                          onChange={(e) => handleFilterChange('minHourlyRate', parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-border rounded text-sm"
                        />
                        <span className="text-text-secondary">-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.maxHourlyRate === 100 ? '' : filters.maxHourlyRate}
                          onChange={(e) => handleFilterChange('maxHourlyRate', parseInt(e.target.value) || 100)}
                          className="w-20 px-2 py-1 border border-border rounded text-sm"
                        />
                      </div>
                    </div>

                    {/* Experience Years */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Experience
                      </label>
                      <select
                        value={`${filters.minExperience}-${filters.maxExperience}`}
                        onChange={(e) => {
                          const [min, max] = e.target.value.split('-').map(v => parseInt(v));
                          handleFilterChange('minExperience', min);
                          handleFilterChange('maxExperience', max);
                        }}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      >
                        <option value="0-20">Any Experience</option>
                        <option value="0-1">Entry Level (0-1 years)</option>
                        <option value="1-3">Junior (1-3 years)</option>
                        <option value="3-5">Mid-Level (3-5 years)</option>
                        <option value="5-10">Senior (5-10 years)</option>
                        <option value="10-20">Expert (10+ years)</option>
                      </select>
                    </div>

                    {/* Skills Filter */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Required Skills
                      </label>
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value && !filters.skills.includes(e.target.value)) {
                            handleFilterChange('skills', [...filters.skills, e.target.value]);
                          }
                        }}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      >
                        <option value="">Add skill...</option>
                        {getUniqueSkills().map(skill => (
                          <option key={skill} value={skill}>{skill}</option>
                        ))}
                      </select>
                      {filters.skills.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {filters.skills.map(skill => (
                            <span
                              key={skill}
                              className="inline-flex items-center space-x-1 px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded"
                            >
                              <span>{skill}</span>
                              <button
                                onClick={() => handleFilterChange('skills', filters.skills.filter(s => s !== skill))}
                                className="text-primary-500 hover:text-primary-700"
                              >
                                <Icon name="X" size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Availability */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Availability
                      </label>
                      <select
                        value={filters.availability}
                        onChange={(e) => handleFilterChange('availability', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      >
                        <option value="all">Any Availability</option>
                        <option value="available">Available Now</option>
                        <option value="busy">Busy</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Location
                      </label>
                      <select
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                      >
                        <option value="all">Any Location</option>
                        {getUniqueLocations().map(location => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Results Summary */}
            {getFilteredAndSortedCandidates().length !== matchingData.recommendations?.length && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Info" size={16} className="text-blue-600" />
                  <span className="text-sm text-blue-800">
                    Showing {Math.min(getFilteredAndSortedCandidates().length, isFreeTier ? 1 : getFilteredAndSortedCandidates().length)} of {getFilteredAndSortedCandidates().length} candidates
                  </span>
                </div>
              </div>
            )}

            {/* Candidate Cards */}
            <div className="space-y-6">
              {getFilteredAndSortedCandidates().slice(0, isFreeTier ? 1 : undefined).map((recommendation, index) => {
                // Track candidate view for free tier users
                const handleCandidateView = async () => {
                  if (isFreeTier && index === 0) {
                    await recordMatchViewed(
                      recommendation.candidate?.id || recommendation.candidate?.user_id,
                      null // jobId is not available in this context
                    );
                  }
                };

                return (
                  <div key={`candidate-${recommendation.candidate?.id || recommendation.candidate?.user_id || index}`} onClick={handleCandidateView}>
                    <CandidateRecommendationCard recommendation={recommendation} />
                  </div>
                );
              })}

              {/* Show upgrade card for free tier users after they've viewed 1 candidate */}
              {isFreeTier && viewedMatchCount > 0 && (
                <UpgradeToViewMoreCard 
                  onUpgradeClick={() => navigate('/pricing')}
                />
              )}
              
              {/* Show skeleton cards when data is still processing */}
              {loading && getFilteredAndSortedCandidates().length === 0 && (
                Array.from({ length: 3 }).map((_, index) => (
                  <CandidateCardSkeleton key={`loading-${index}`} delay={index * 100} />
                ))
              )}
            </div>

            {/* No Results Message */}
            {getFilteredAndSortedCandidates().length === 0 && matchingData.recommendations?.length > 0 && (
              <div className="bg-surface rounded-lg border border-border p-8 text-center">
                <Icon name="Search" size={48} className="text-secondary-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">No candidates found</h3>
                <p className="text-text-secondary mb-4">
                  Try adjusting your search criteria or filters to find more candidates.
                </p>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSmartSearch = () => {
    if (loading) {
      return <AIMatchingSkeleton section="smart-search" />;
    }
    
    return (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">AI-Enhanced Search Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Optimal Hourly Rate Range
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={matchingData.smart_filters?.optimal_hourly_rate?.min}
                className="w-20 px-3 py-2 border border-border rounded-lg"
                readOnly
              />
              <span className="text-text-secondary">to</span>
              <input
                type="number"
                value={matchingData.smart_filters?.optimal_hourly_rate?.max}
                className="w-20 px-3 py-2 border border-border rounded-lg"
                readOnly
              />
            </div>
            <p className="text-xs text-text-muted mt-1">AI-recommended based on market data</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Recommended Experience
            </label>
            <input
              type="text"
              value={matchingData.smart_filters?.recommended_experience}
              className="w-full px-3 py-2 border border-border rounded-lg"
              readOnly
            />
            <p className="text-xs text-text-muted mt-1">Optimal for your requirements</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Best Time Zones
            </label>
            <div className="flex flex-wrap gap-2">
              {matchingData.smart_filters?.best_time_zones?.map((zone, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  {zone}
                </span>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-1">Maximum overlap with your schedule</p>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Market Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-primary mb-1">
              {matchingData.market_insights?.average_response_time}
            </div>
            <div className="text-sm text-text-secondary">Avg Response Time</div>
          </div>
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 mb-1">
              {matchingData.market_insights?.competition_level}
            </div>
            <div className="text-sm text-text-secondary">Competition Level</div>
          </div>
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {matchingData.market_insights?.salary_trends}
            </div>
            <div className="text-sm text-text-secondary">Salary Trends</div>
          </div>
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {matchingData.market_insights?.availability_forecast}
            </div>
            <div className="text-sm text-text-secondary">Availability</div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderInsights = () => {
    if (loading) {
      return <AIMatchingSkeleton section="insights" />;
    }
    
    return (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Optimization Suggestions</h3>
        <div className="space-y-4">
          {aiInsights.optimization_suggestions?.map((suggestion, index) => (
            <div key={index} className="flex items-start space-x-4 p-4 bg-secondary-50 rounded-lg">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                suggestion.impact === 'High' ? 'bg-red-100' :
                suggestion.impact === 'Medium' ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                <Icon name="Lightbulb" size={16} className={
                  suggestion.impact === 'High' ? 'text-red-600' :
                  suggestion.impact === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                } />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-text-primary">{suggestion.category}</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      suggestion.impact === 'High' ? 'bg-red-100 text-red-800' :
                      suggestion.impact === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {suggestion.impact} Impact
                    </span>
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      {suggestion.effort} Effort
                    </span>
                  </div>
                </div>
                <p className="text-text-secondary text-sm">{suggestion.suggestion}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Success Factors Analysis</h3>
        <div className="space-y-4">
          {aiInsights.success_factors?.map((factor, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary">{factor.factor}</span>
                <span className="text-sm text-text-secondary">{factor.your_score}/100</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <MatchScoreBar score={factor.your_score} size="sm" />
                </div>
                <span className="text-xs text-text-muted w-16">
                  {factor.importance}% important
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const renderPredictions = () => {
    if (loading) {
      return <AIMatchingSkeleton section="predictions" />;
    }
    
    return (
    <div className="space-y-6">
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">30-Day Hiring Forecast</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {aiInsights.hiring_predictions?.next_30_days?.expected_applications}
            </div>
            <div className="text-sm text-text-secondary">Expected Applications</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {aiInsights.hiring_predictions?.next_30_days?.quality_score}/10
            </div>
            <div className="text-sm text-text-secondary">Quality Score</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600 mb-1">
              {aiInsights.hiring_predictions?.next_30_days?.interview_likelihood}%
            </div>
            <div className="text-sm text-text-secondary">Interview Likelihood</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {aiInsights.hiring_predictions?.next_30_days?.hire_probability}%
            </div>
            <div className="text-sm text-text-secondary">Hire Probability</div>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Seasonal Trends</h3>
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="TrendingUp" size={24} className="text-green-600" />
            <div>
              <h4 className="font-semibold text-green-800">
                {aiInsights.hiring_predictions?.seasonal_trends?.current_season}
              </h4>
              <p className="text-green-700 text-sm">
                {aiInsights.hiring_predictions?.seasonal_trends?.recommendation}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-text-secondary">Demand Level</span>
              <div className="text-lg font-semibold text-text-primary">
                {aiInsights.hiring_predictions?.seasonal_trends?.demand_level}
              </div>
            </div>
            <div>
              <span className="text-sm text-text-secondary">Competition</span>
              <div className="text-lg font-semibold text-text-primary">
                {aiInsights.hiring_predictions?.seasonal_trends?.competition}
              </div>
            </div>
            <div>
              <span className="text-sm text-text-secondary">Best Posting Time</span>
              <div className="text-lg font-semibold text-text-primary">
                {matchingData.market_insights?.recommended_posting_time}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderComingSoon = (featureName, description, icon) => (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="w-20 h-20 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-center justify-center mb-6">
        <Icon name={icon} size={36} className="text-amber-500" />
      </div>
      <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full border border-amber-200 mb-4">
        <Icon name="Clock" size={12} />
        <span>Coming Soon</span>
      </div>
      <h2 className="text-2xl font-bold text-text-primary mb-3">{featureName}</h2>
      <p className="text-text-secondary max-w-md leading-relaxed mb-8">{description}</p>
      <div className="flex items-center space-x-2 text-sm text-text-muted">
        <Icon name="Wrench" size={14} />
        <span>This feature is currently under development. Check back soon!</span>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'recommendations':
        return renderRecommendations();
      case 'smart-search':
        return renderComingSoon(
          'Smart Search',
          'AI-enhanced search filters with optimal rate ranges, recommended experience levels, and best time zone matching — powered by real market data.',
          'Search'
        );
      case 'insights':
        return renderComingSoon(
          'AI Insights',
          'Deep optimization suggestions and success factor analysis to help you craft better job postings and attract higher-quality candidates.',
          'Brain'
        );
      case 'predictions':
        return renderComingSoon(
          'Predictions',
          '30-day hiring forecasts, seasonal demand trends, and AI-driven hiring probability scores to help you plan your recruitment strategy.',
          'TrendingUp'
        );
      default:
        return renderRecommendations();
    }
  };

  return (
    <div className="min-h-screen bg-background">
        <EmployerHeaderNav />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Auto Matching</h1>
                <p className="text-text-secondary">Find the perfect candidates with advanced auto recommendations</p>
              </div>
            </div>
          </div>

          {/* Free Tier - Show Upgrade Prompt After Limit Reached */}
          {isFreeTier && !canViewMore ? (
            <FreeTierUpgradePrompt
              onUpgradeClick={() => navigate('/pricing')}
              onDismiss={() => navigate('/employer-dashboard')}
            />
          ) : (
            <>
              {/* Free Tier Match Limit Banner */}
              {isFreeTier && (
                <FreeUserMatchLimitBanner 
                  matchCount={viewedMatchCount} 
                  remainingMatches={remainingMatches}
                  isFreeTier={isFreeTier}
                />
              )}
              
              {/* Tabs */}
              <div className="bg-surface rounded-lg border border-border mb-6 overflow-x-auto">
                <div className="flex min-w-full">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => !tab.comingSoon && setActiveTab(tab.id)}
                      className={`relative flex items-center justify-center space-x-1 px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 text-xs sm:text-sm md:text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap flex-1 sm:flex-none ${
                        tab.comingSoon
                          ? 'border-transparent text-text-muted cursor-not-allowed opacity-60'
                          : activeTab === tab.id
                          ? 'border-primary text-primary bg-primary-50'
                          : 'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                      }`}
                    >
                      <Icon name={tab.icon} size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      {tab.comingSoon && (
                        <span className="hidden sm:inline ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full leading-none border border-amber-200">
                          Soon
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </>
          )}
        </div>
      </main>
</div>
  );
};

export default AIMatchingPage;


